import React, { useState, useEffect } from 'react';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { useRoleStore } from '@/core/application/state/useRoleStore';
import { ProfessionalLayout } from '@/modules/professional/layouts/ProfessionalLayout';
import { useNavigate } from 'react-router-dom';
import { useViewport } from '@/shared/hooks/useViewport';
import { X, Plus, Trash2, PlusCircle, Calendar, Clock, Coffee, CheckCircle, FileText, DollarSign } from 'lucide-react';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface Intervalo {
  id: string;
  inicio: string;
  fin: string;
}

interface ConfigDia {
  activo: boolean;
  intervalos: Intervalo[];
}

interface AgendaConfig {
  duracion_minutos: number;
  brecha_minutos: number;
  anticipacion_horas?: number;
  dias: Record<string, ConfigDia>;
}

interface GrupoHorario {
  id: string;
  dias: string[];
  intervalos: Intervalo[];
}

// ─── Constantes ───────────────────────────────────────────────────────────────

const DIAS_ORDEN = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
const DIAS_ABBR: Record<string, string> = {
  lunes: 'L', martes: 'M', 'miércoles': 'X',
  jueves: 'J', viernes: 'V', sábado: 'S', domingo: 'D',
};

const HORAS: string[] = [];
for (let h = 6; h <= 22; h++) {
  HORAS.push(`${String(h).padStart(2, '0')}:00`);
  if (h < 22) HORAS.push(`${String(h).padStart(2, '0')}:30`);
}

const DURACION_OPTS = [15, 20, 30, 45, 60, 90, 120].map(m => ({
  label: m < 60 ? `${m} min` : `${m / 60}h`,
  value: String(m),
}));

const BRECHA_OPTS = [0, 5, 10, 15, 20, 30].map(m => ({
  label: m === 0 ? 'Sin pausa' : `${m} min`,
  value: String(m),
}));

const ANTICIPACION_OPTS = [
  { label: 'Sin anticipación', value: '0' },
  { label: '1 hora antes', value: '1' },
  { label: '2 horas antes', value: '2' },
  { label: '12 horas antes', value: '12' },
  { label: '24 horas antes', value: '24' },
  { label: '48 horas antes', value: '48' },
];

function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

function formatHora(h: string): string {
  const [hr, mn] = h.split(':').map(Number);
  const ampm = hr >= 12 ? 'PM' : 'AM';
  return `${hr % 12 || 12}:${String(mn).padStart(2, '0')} ${ampm}`;
}

// ─── Lógica de Agrupación ─────────────────────────────────────────────────────

function groupDias(diasObj: Record<string, ConfigDia>): GrupoHorario[] {
  const grupos: GrupoHorario[] = [];
  for (const dia of DIAS_ORDEN) {
    const cfg = diasObj[dia];
    if (!cfg?.activo) continue;
    
    const sig = JSON.stringify(cfg.intervalos.map(i => ({ i: i.inicio, f: i.fin })));
    const existing = grupos.find(g => (g as any)._sig === sig);
    
    if (existing) {
      existing.dias.push(dia);
    } else {
      grupos.push({
        id: uid(),
        dias: [dia],
        intervalos: cfg.intervalos.map(i => ({ ...i, id: uid() })),
        _sig: sig
      } as GrupoHorario);
    }
  }
  return grupos;
}

function ungroupDias(grupos: GrupoHorario[]): Record<string, ConfigDia> {
  const dias: Record<string, ConfigDia> = {};
  for (const d of DIAS_ORDEN) {
    dias[d] = { activo: false, intervalos: [] };
  }
  for (const g of grupos) {
    for (const d of g.dias) {
      dias[d] = {
        activo: true,
        intervalos: g.intervalos.map(i => ({ id: uid(), inicio: i.inicio, fin: i.fin }))
      };
    }
  }
  return dias;
}

function defaultConfig(): AgendaConfig {
  const dias: Record<string, ConfigDia> = {};
  DIAS_ORDEN.forEach((d, i) => {
    dias[d] = {
      activo: i < 5,
      intervalos: [{ id: uid(), inicio: '08:00', fin: '17:00' }],
    };
  });
  return { duracion_minutos: 45, brecha_minutos: 15, anticipacion_horas: 24, dias };
}

// ─── Componentes Hijos ────────────────────────────────────────────────────────

function TimeChip({ value, onChange, min }: Readonly<{
  value: string; onChange: (v: string) => void; min?: string;
}>) {
  const opts = (min ? HORAS.filter(h => h > min) : HORAS).map(h => ({ label: formatHora(h), value: h }));
  return (
    <div style={{ flex: 1, backgroundColor: '#F1F5F9', borderRadius: '10px', overflow: 'hidden' }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          border: 'none',
          color: '#0F172A',
          fontSize: '14px',
          fontWeight: 600,
          padding: '8px',
          outline: 'none',
          cursor: 'pointer'
        }}
      >
        {opts.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

// ─── Lógica Pura de Mutación (Sin Stale Closures ni Deep Nesting) ───────────

function applyToggleDia(prev: GrupoHorario[], grupoId: string, dia: string) {
  return prev.map((g: any) => {
    let nuevas = g.dias;
    if (g.id === grupoId) {
      nuevas = g.dias.includes(dia) ? g.dias.filter((d: string) => d !== dia) : [...g.dias, dia];
    } else if (g.dias.includes(dia)) {
      nuevas = g.dias.filter((d: string) => d !== dia);
    }
    return { ...g, dias: nuevas };
  }).filter((g: any) => g.dias.length > 0 || g.id === grupoId);
}

function applyUpdateIntervalo(prev: GrupoHorario[], grupoId: string, ivId: string, campo: 'inicio' | 'fin', val: string) {
  return prev.map((g: any) => 
    g.id === grupoId 
      ? { ...g, intervalos: g.intervalos.map((iv: any) => iv.id === ivId ? { ...iv, [campo]: val } : iv) }
      : g
  );
}

function applyAddIntervalo(prev: GrupoHorario[], grupoId: string) {
  return prev.map((g: any) => {
    if (g.id !== grupoId) return g;
    const ultimo = g.intervalos.at(-1);
    const start = ultimo?.fin ?? '08:00';
    const [h] = start.split(':').map(Number);
    const end = `${String(Math.min(h + 1, 22)).padStart(2, '0')}:00`;
    return { ...g, intervalos: [...g.intervalos, { id: uid(), inicio: start, fin: end }] };
  });
}

function applyRemoveIntervalo(prev: GrupoHorario[], grupoId: string, ivId: string) {
  return prev.map((g: any) => 
    g.id === grupoId 
      ? { ...g, intervalos: g.intervalos.filter((iv: any) => iv.id !== ivId) }
      : g
  );
}

// ─── Sub-Componentes ──────────────────────────────────────────────────────────

function DisponibilidadCard({ 
  grupos, primary, handleDeleteGroup, handleToggleDia, 
  handleUpdateIntervalo, handleAddIntervalo, handleRemoveIntervalo, handleAddGroup 
}: any) {
  return (
    <>
      {grupos.map((grupo: any) => (
        <div key={grupo.id} style={{ position: 'relative', marginBottom: '16px' }}>
          <button
            onClick={() => handleDeleteGroup(grupo.id)}
            style={{ position: 'absolute', top: '12px', right: '12px', background: 'none', border: 'none', cursor: 'pointer', zIndex: 10, padding: '4px' }}
          >
            <X size={20} color="#94A3B8" />
          </button>

          <div style={{ backgroundColor: '#FFF', borderRadius: '16px', padding: '20px', border: '1px solid #E2E8F0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <p style={{ fontSize: '13px', fontWeight: 600, color: '#64748B', margin: '0 0 12px 0' }}>Aplica para los días:</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {DIAS_ORDEN.map(dia => {
                const isSelected = grupo.dias.includes(dia);
                return (
                  <button
                    key={dia}
                    onClick={() => handleToggleDia(grupo.id, dia)}
                    style={{
                      width: '36px', height: '36px', borderRadius: '18px',
                      backgroundColor: isSelected ? primary : '#F8FAFC',
                      border: `1px solid ${isSelected ? primary : '#E2E8F0'}`,
                      color: isSelected ? '#FFF' : '#475569',
                      fontWeight: 600, cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center',
                      transition: 'all 0.2s'
                    }}
                  >
                    {DIAS_ABBR[dia]}
                  </button>
                );
              })}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #F1F5F9', margin: '20px 0' }} />

            <p style={{ fontSize: '13px', fontWeight: 600, color: '#64748B', margin: '0 0 12px 0' }}>En el siguiente horario:</p>
            {grupo.intervalos.map((iv: any, iIdx: number) => (
              <div key={iv.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                <TimeChip value={iv.inicio} onChange={(v: string) => handleUpdateIntervalo(grupo.id, iv.id, 'inicio', v)} />
                <span style={{ fontSize: '16px', color: '#94A3B8', fontWeight: 500, margin: '0 8px' }}>-</span>
                <TimeChip value={iv.fin} onChange={(v: string) => handleUpdateIntervalo(grupo.id, iv.id, 'fin', v)} min={iv.inicio} />
                
                <div style={{ width: '44px', display: 'flex', justifyContent: 'flex-end', marginLeft: '8px' }}>
                  {iIdx === 0 ? (
                    <button onClick={() => handleAddIntervalo(grupo.id)} style={{ width: '34px', height: '34px', borderRadius: '17px', border: 'none', backgroundColor: `${primary}15`, color: primary, cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Plus size={18} />
                    </button>
                  ) : (
                    <button onClick={() => handleRemoveIntervalo(grupo.id, iv.id)} style={{ width: '34px', height: '34px', borderRadius: '17px', border: 'none', backgroundColor: '#FEF2F2', color: '#EF4444', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <button
        onClick={handleAddGroup}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
          width: '100%', padding: '12px', marginTop: '8px',
          backgroundColor: '#F8FAFC', borderRadius: '12px',
          border: '1px dashed #E2E8F0', color: primary, fontWeight: 600, cursor: 'pointer'
        }}
      >
        <PlusCircle size={20} />
        Agregar horario diferente
      </button>
    </>
  );
}

function ConfiguracionGeneralCard({ 
  primary, duracion, setDuracion, brecha, setBrecha, anticipacion, setAnticipacion,
  aceptaPagoAnticipado, setAceptaPagoAnticipado, aceptaPagoPostReserva, setAceptaPagoPostReserva,
  onOpenPoliticas
}: any) {
  
  // Calculate value for the select
  let pagoValue = 'ambos';
  if (aceptaPagoAnticipado && !aceptaPagoPostReserva) pagoValue = 'anticipado';
  if (!aceptaPagoAnticipado && aceptaPagoPostReserva) pagoValue = 'post_reserva';

  const handlePagoChange = (val: string) => {
    if (val === 'anticipado') {
      setAceptaPagoAnticipado(true);
      setAceptaPagoPostReserva(false);
    } else if (val === 'post_reserva') {
      setAceptaPagoAnticipado(false);
      setAceptaPagoPostReserva(true);
    } else {
      setAceptaPagoAnticipado(true);
      setAceptaPagoPostReserva(true);
    }
  };

  return (
    <div style={{
      backgroundColor: '#FFF', borderRadius: '16px', padding: '20px',
      border: `1px solid ${primary}20`, position: 'relative', overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
    }}>
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', backgroundColor: primary }} />
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px', marginBottom: '16px' }}>
        <div style={{ flex: 1 }}>
          <label htmlFor="select-duracion" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: '8px' }}>
            <Clock size={14} /> Duración (min)
          </label>
          <div style={{ backgroundColor: '#F1F5F9', borderRadius: '10px', padding: '0 8px' }}>
            <select id="select-duracion" value={duracion} onChange={e => setDuracion(e.target.value)} style={{ width: '100%', border: 'none', background: 'transparent', padding: '8px 16px 8px 0', outline: 'none', fontWeight: 600, color: '#0F172A', textOverflow: 'ellipsis', boxSizing: 'border-box' }}>
              {DURACION_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ flex: 1 }}>
          <label htmlFor="select-brecha" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: '8px' }}>
            <Coffee size={14} /> Pausa (min)
          </label>
          <div style={{ backgroundColor: '#F1F5F9', borderRadius: '10px', padding: '0 8px' }}>
            <select id="select-brecha" value={brecha} onChange={e => setBrecha(e.target.value)} style={{ width: '100%', border: 'none', background: 'transparent', padding: '8px 16px 8px 0', outline: 'none', fontWeight: 600, color: '#0F172A', textOverflow: 'ellipsis', boxSizing: 'border-box' }}>
              {BRECHA_OPTS.map(o => <option key={o.label} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ flex: 1 }}>
          <label htmlFor="select-anticipacion" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: '8px' }}>
            <Calendar size={14} /> Anticipación
          </label>
          <div style={{ backgroundColor: '#F1F5F9', borderRadius: '10px', padding: '0 8px' }}>
            <select id="select-anticipacion" value={anticipacion} onChange={e => setAnticipacion(e.target.value)} style={{ width: '100%', border: 'none', background: 'transparent', padding: '8px 16px 8px 0', outline: 'none', fontWeight: 600, color: '#0F172A', textOverflow: 'ellipsis', boxSizing: 'border-box' }}>
              {ANTICIPACION_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ flex: 1 }}>
          <label htmlFor="select-pago" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: '8px' }}>
            <DollarSign size={14} /> Modo de Pago
          </label>
          <div style={{ backgroundColor: '#F1F5F9', borderRadius: '10px', padding: '0 8px' }}>
            <select id="select-pago" value={pagoValue} onChange={e => handlePagoChange(e.target.value)} style={{ width: '100%', border: 'none', background: 'transparent', padding: '8px 16px 8px 0', outline: 'none', fontWeight: 600, color: '#0F172A', textOverflow: 'ellipsis', boxSizing: 'border-box' }}>
              <option value="ambos">Ambas opciones</option>
              <option value="anticipado">Solo Anticipado</option>
              <option value="post_reserva">Solo en Clínica</option>
            </select>
          </div>
        </div>
      </div>

      <button
        onClick={onOpenPoliticas}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          width: '100%', padding: '12px', backgroundColor: '#F8FAFC', borderRadius: '10px',
          border: '1px solid #E2E8F0', color: '#0F172A', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s'
        }}
      >
        <FileText size={18} color={primary} />
        Redactar Políticas de Agendamiento
      </button>

    </div>
  );
}



// ─── Hook Principal ──────────────────────────────────────────────────────────

function useAgendaConfig() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profUuid, setProfUuid] = useState<string | null>(null);

  const [toastMessage, setToastMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);

  const showToast = (message: string) => {
    setToastMessage(message);
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 3500);
  };

  const [duracion, setDuracion] = useState('45');
  const [brecha, setBrecha] = useState('15');
  const [anticipacion, setAnticipacion] = useState('24');
  const [grupos, setGrupos] = useState<GrupoHorario[]>([]);
  const [politicas, setPoliticas] = useState('');
  const [aceptaPagoAnticipado, setAceptaPagoAnticipado] = useState(true);
  const [aceptaPagoPostReserva, setAceptaPagoPostReserva] = useState(true);

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/hub');
      return;
    }
    setProfUuid(user.id);

    const { data } = await supabase
      .from('profesional_config_agenda')
      .select('disponibilidad_jsonb, politicas_agendamiento, acepta_pago_post_reserva, acepta_pago_anticipado')
      .eq('profesional_uuid', user.id)
      .maybeSingle();

    let raw = data?.disponibilidad_jsonb as unknown as AgendaConfig;
    if (!raw?.dias) raw = defaultConfig();

    setDuracion(String(raw.duracion_minutos || 45));
    setBrecha(String(raw.brecha_minutos || 15));
    setAnticipacion(String(raw.anticipacion_horas ?? 24));
    setGrupos(groupDias(raw.dias));
    setPoliticas(data?.politicas_agendamiento || '');
    setAceptaPagoAnticipado(data?.acepta_pago_anticipado ?? true);
    setAceptaPagoPostReserva(data?.acepta_pago_post_reserva ?? true);
    setLoading(false);
  };

  const handleToggleDia = (grupoId: string, dia: string) => {
    setGrupos(prev => applyToggleDia(prev, grupoId, dia));
  };

  const handleUpdateIntervalo = (grupoId: string, ivId: string, campo: 'inicio' | 'fin', val: string) => {
    setGrupos(prev => applyUpdateIntervalo(prev, grupoId, ivId, campo, val));
  };

  const handleAddIntervalo = (grupoId: string) => {
    setGrupos(prev => applyAddIntervalo(prev, grupoId));
  };

  const handleRemoveIntervalo = (grupoId: string, ivId: string) => {
    setGrupos(prev => applyRemoveIntervalo(prev, grupoId, ivId));
  };

  const handleAddGroup = () => {
    setGrupos(prev => [
      ...prev,
      { id: uid(), dias: [], intervalos: [{ id: uid(), inicio: '08:00', fin: '17:00' }] }
    ]);
  };

  const handleDeleteGroup = (grupoId: string) => {
    setGrupos(prev => prev.filter(g => g.id !== grupoId));
  };

  const guardar = async () => {
    if (!profUuid) return;
    
    const gruposLimpios = grupos.filter(g => g.dias.length > 0);

    for (const g of gruposLimpios) {
      if (g.intervalos.length === 0) {
        globalThis.alert('Error: Hay un grupo de días sin horas asignadas.'); return;
      }
      for (const iv of g.intervalos) {
        if (iv.fin <= iv.inicio) {
          globalThis.alert('Hora inválida: La hora de fin debe ser posterior a la de inicio.'); return;
        }
      }
    }

    setSaving(true);
    const diasObj = ungroupDias(gruposLimpios);
    const payload: AgendaConfig = {
      duracion_minutos: Number(duracion),
      brecha_minutos: Number(brecha),
      anticipacion_horas: Number(anticipacion),
      dias: diasObj
    };

    const { error } = await supabase
      .from('profesional_config_agenda')
      .upsert({ 
        profesional_uuid: profUuid, 
        disponibilidad_jsonb: payload as any,
        politicas_agendamiento: politicas.trim(),
        acepta_pago_anticipado: aceptaPagoAnticipado,
        acepta_pago_post_reserva: aceptaPagoPostReserva
      }, { onConflict: 'profesional_uuid' });
    
    setSaving(false);
    if (error) {
      globalThis.alert('Error: No se pudo guardar la configuración.');
    } else {
      showToast('¡Éxito! Horario actualizado correctamente.');
    }
  };

  return {
    navigate,
    loading, saving, toastMessage, toastVisible,
    duracion, setDuracion, brecha, setBrecha, anticipacion, setAnticipacion,
    grupos, politicas, setPoliticas, aceptaPagoAnticipado, setAceptaPagoAnticipado,
    aceptaPagoPostReserva, setAceptaPagoPostReserva,
    handleToggleDia, handleUpdateIntervalo, handleAddIntervalo, handleRemoveIntervalo,
    handleAddGroup, handleDeleteGroup, guardar
  };
}

export function AgendaConfigScreen() {
  const { isMobile } = useViewport();
  const theme = useRoleStore(s => s.getTheme());
  const primary = theme.primary ?? '#0052CC';
  const [modalPoliticasOpen, setModalPoliticasOpen] = useState(false);

  const {
    navigate,
    loading, saving, toastMessage, toastVisible,
    duracion, setDuracion, brecha, setBrecha, anticipacion, setAnticipacion,
    grupos, politicas, setPoliticas, aceptaPagoAnticipado, setAceptaPagoAnticipado,
    aceptaPagoPostReserva, setAceptaPagoPostReserva,
    handleToggleDia, handleUpdateIntervalo, handleAddIntervalo, handleRemoveIntervalo,
    handleAddGroup, handleDeleteGroup, guardar
  } = useAgendaConfig();

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ width: 40, height: 40, border: `4px solid ${primary}40`, borderTopColor: primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <ProfessionalLayout>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%', paddingBottom: '80px', position: 'relative' }}>
        
        {/* HEADER interno */}
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          backgroundColor: '#FFF', padding: '14px 16px', borderBottom: '1px solid #F1F5F9',
          borderRadius: '12px', marginBottom: '24px'
        }}>
          <h1 style={{ fontSize: '18px', fontWeight: 700, margin: 0, color: '#0F172A' }}>Horario de Atención</h1>
          <button
            onClick={guardar}
            disabled={saving}
            style={{
              backgroundColor: primary, color: '#FFF', padding: '8px 16px',
              borderRadius: '100px', fontWeight: 600, border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1
            }}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </header>

        <div style={{ maxWidth: '640px', width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '32px' }}>
          
          <ConfiguracionGeneralCard 
            primary={primary} 
            duracion={duracion} setDuracion={setDuracion}
            brecha={brecha} setBrecha={setBrecha}
            anticipacion={anticipacion} setAnticipacion={setAnticipacion}
            aceptaPagoAnticipado={aceptaPagoAnticipado} setAceptaPagoAnticipado={setAceptaPagoAnticipado}
            aceptaPagoPostReserva={aceptaPagoPostReserva} setAceptaPagoPostReserva={setAceptaPagoPostReserva}
            onOpenPoliticas={() => setModalPoliticasOpen(true)}
          />

          <div>
            <h2 style={{ fontSize: '13px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px', marginLeft: '4px' }}>
              Disponibilidad
            </h2>
            <DisponibilidadCard 
              grupos={grupos} 
              primary={primary} 
              handleDeleteGroup={handleDeleteGroup}
              handleToggleDia={handleToggleDia}
              handleUpdateIntervalo={handleUpdateIntervalo}
              handleAddIntervalo={handleAddIntervalo}
              handleRemoveIntervalo={handleRemoveIntervalo}
              handleAddGroup={handleAddGroup}
            />
          </div>
        </div>

        {/* FAB Calendario (Flotante fijo) */}
        <button
          onClick={() => navigate('/pro/CalendarioSemanal')}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          title="Ver Calendario Semanal"
          style={{
            position: 'fixed', bottom: isMobile ? '100px' : '24px', right: '24px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: primary, width: '56px', height: '56px', borderRadius: '28px',
            color: '#FFFFFF', border: 'none', cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)', transition: 'transform 0.2s, bottom 0.3s ease', zIndex: 100
          }}
        >
          <Calendar size={24} />
        </button>
        {/* Toast */}
        {toastVisible && (
          <div style={{
            position: 'fixed', bottom: '40px', left: '50%', transform: 'translateX(-50%)',
            backgroundColor: '#1F2937', padding: '12px 20px', borderRadius: '24px',
            display: 'flex', flexDirection: 'row', alignItems: 'center',
            boxShadow: '0px 4px 10px rgba(0,0,0,0.2)', zIndex: 9999, animation: 'fadeIn 0.3s ease'
          }}>
            <CheckCircle size={20} color="#FFF" style={{ marginRight: '8px' }} />
            <span style={{ color: '#FFF', fontSize: '14px', fontWeight: 600 }}>{toastMessage}</span>
          </div>
        )}

        {/* Modal de Políticas */}
        {modalPoliticasOpen && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: '20px'
          }}>
            <div style={{ backgroundColor: '#FFF', borderRadius: '16px', padding: '24px', maxWidth: '500px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 8px 0', color: '#0F172A', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={20} color={primary} />
                Políticas de Agendamiento
              </h2>
              <p style={{ fontSize: '13px', color: '#64748B', marginBottom: '16px', lineHeight: 1.5 }}>
                Establece términos claros para tus pacientes (ej. cancelaciones, tiempos máximos de espera). El paciente deberá aceptarlas obligatoriamente.
              </p>
              <textarea
                value={politicas}
                onChange={(e) => setPoliticas(e.target.value)}
                placeholder="Ej: Las cancelaciones deben hacerse con al menos 24 horas de anticipación..."
                style={{
                  width: '100%', minHeight: '140px', padding: '12px', borderRadius: '8px',
                  border: '1px solid #CBD5E1', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', outline: 'none'
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
                <button
                  onClick={() => setModalPoliticasOpen(false)}
                  style={{ backgroundColor: primary, color: '#FFF', border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Guardar y Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </ProfessionalLayout>
  );
}
